import { useCallback, useEffect, useRef, type RefObject } from 'react';

/**
 * Two-way selection sync with the host: local selections are forwarded without scrolling;
 * a selection that arrives from outside scrolls its anchor into view. Escape deselects.
 */
export function usePriceBookSelectionSync(
  rootRef: RefObject<HTMLElement | null>,
  selectedId: string | null,
  select: (id: string | null) => void
): (id: string | null) => void {
  const lastLocalRef = useRef<string | null>(null);

  const selectLocal = useCallback(
    (id: string | null) => {
      lastLocalRef.current = id;
      select(id);
    },
    [select]
  );

  useEffect(() => {
    if (!selectedId || lastLocalRef.current === selectedId) return;
    const anchors = rootRef.current?.querySelectorAll<HTMLElement>('[data-price-book-selection-id]') ?? [];
    const target = Array.from(anchors).find((element) => element.dataset.priceBookSelectionId === selectedId);
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
  }, [rootRef, selectedId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      lastLocalRef.current = null;
      select(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedId, select]);

  return selectLocal;
}
